import { View } from "@animech/camera";
import { SceneEngine } from "@animech/axiom-scene-utilities";
import debounce from "lodash.debounce";
import { BoundingBox, math, Quat, Vec3 } from "playcanvas";
import { CameraSystem } from "src/scene/camera-system";
import { config as sceneConfig } from "src/scene/scene-manager";
import { horizontalFovFromVerticalFov } from "src/utility/camera-utility";

type DebouncedFn = ReturnType<typeof debounce>;

export type OrbitAngles = { pitch: number; yaw: number };

export type ViewerCameraFitterOptions = {
	cameraSystem: CameraSystem;
	sceneEngine: SceneEngine;
	canvas: HTMLCanvasElement;
	bbox: BoundingBox;
	vFov: number;
	orbit: OrbitAngles;
	paddingFactor: number;
	maxDistancePadding: number;
};

/**
 * Keeps the viewer's orbit camera framed on its product as the host iframe
 * resizes.
 *
 * Owns: projected-bbox fit math, a ResizeObserver on the canvas, and the
 * scene-engine resize handoff. Construct once after camera + canvas + bbox
 * are known, call `fit()` for the first frame, and `dispose()` on teardown.
 *
 * Resize handling debounces along scene-manager's pattern: trailing-only at
 * `resizeDebounceTime` for the canvas resize and `resizeEndDebounceTime` for
 * the camera fit. Reusing those constants keeps both configurators sharing a
 * single source of truth for resize cadence. Without debouncing, each
 * ResizeObserver fire would clear the canvas drawing buffer (HTML5 canvas
 * width/height assignment erases the buffer) before the next render landed —
 * producing a visible flicker through to the transparent body background.
 *
 * Lives in the viewer-application folder, not `src/scene/`, because it
 * implements the viewer's specific "always-framed product" UX. The
 * non-viewer entry point lets users freely position their camera and has no
 * need for this. If a second consumer ever appears, we'll move it up.
 */
export class ViewerCameraFitter {
	private readonly _opts: ViewerCameraFitterOptions;
	private _resizeObserver?: ResizeObserver;
	private readonly _debouncedResize: DebouncedFn;
	private readonly _debouncedFit: DebouncedFn;

	constructor(opts: ViewerCameraFitterOptions) {
		this._opts = opts;
		this._debouncedResize = debounce(
			() => this._resizeCanvas(),
			sceneConfig.resizeDebounceTime,
		);
		this._debouncedFit = debounce(
			() => this.fit(),
			sceneConfig.resizeEndDebounceTime,
		);
		this._observeCanvasResize();
	}

	fit(): void {
		const {
			cameraSystem,
			sceneEngine,
			bbox,
			vFov,
			orbit,
			paddingFactor,
			maxDistancePadding,
		} = this._opts;
		const device = sceneEngine.Application.graphicsDevice;
		const aspect = device.width / device.height;
		const hFov = horizontalFovFromVerticalFov(vFov, aspect);
		const cameraRotation = new Quat().setFromEulerAngles(
			orbit.pitch,
			orbit.yaw,
			0,
		);
		const distance = fitDistanceToBoundingBox(
			bbox,
			cameraRotation,
			hFov,
			vFov,
			paddingFactor,
		);
		cameraSystem.settings.maxDistance = distance + maxDistancePadding;
		const view = new View().setOrbit(
			distance,
			orbit.pitch,
			orbit.yaw,
			bbox.center,
		);
		void cameraSystem.setView(view, bbox.center, "instant");
	}

	dispose(): void {
		this._resizeObserver?.disconnect();
		this._resizeObserver = undefined;
		this._debouncedResize.cancel();
		this._debouncedFit.cancel();
	}

	private _observeCanvasResize(): void {
		if (typeof ResizeObserver === "undefined") {
			return;
		}
		this._resizeObserver = new ResizeObserver(() => this._handleResize());
		this._resizeObserver.observe(this._opts.canvas);
	}

	private _handleResize(): void {
		this._debouncedResize();
		this._debouncedFit();
	}

	private _resizeCanvas(): void {
		const { cameraSystem, sceneEngine, canvas } = this._opts;
		const rect = canvas.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) {
			return;
		}
		sceneEngine.resizeCanvas(rect.width, rect.height);
		const device = sceneEngine.Application.graphicsDevice;
		cameraSystem.onDeviceResize({
			width: device.width,
			height: device.height,
		});
	}
}

/**
 * Distance at which the camera, rotated to `cameraRotation`, frames `bbox`.
 *
 * Per AABB corner the projection requires |ndc| ≤ 1, which rearranges to
 * `d ≥ corner.z + |corner.{x,y}| / tan(fov/2)` in camera space. The
 * `corner.z` term is perspective foreshortening — a corner with positive
 * camera-local z sits closer to the camera and projects larger than its
 * half-extent suggests, so it needs more distance to fit. Dropping it
 * under-frames wide-and-low products at oblique orbits (Bench 9 at 25°/25°:
 * the long axis swings a corner toward the camera).
 *
 * Module-private. Lift to `src/utility/camera-utility.ts` if a non-viewer
 * consumer ever needs it.
 */
function fitDistanceToBoundingBox(
	bbox: BoundingBox,
	cameraRotation: Quat,
	hFov: number,
	vFov: number,
	paddingFactor: number,
): number {
	const worldToCamera = cameraRotation.clone().invert();
	const h = bbox.halfExtents;
	const tanH = Math.tan(hFov * 0.5 * math.DEG_TO_RAD);
	const tanV = Math.tan(vFov * 0.5 * math.DEG_TO_RAD);
	const corner = new Vec3();
	let d = 0;
	for (let sx = -1; sx <= 1; sx += 2) {
		for (let sy = -1; sy <= 1; sy += 2) {
			for (let sz = -1; sz <= 1; sz += 2) {
				corner.set(sx * h.x, sy * h.y, sz * h.z);
				worldToCamera.transformVector(corner, corner);
				d = Math.max(d, corner.z + Math.abs(corner.x) / tanH);
				d = Math.max(d, corner.z + Math.abs(corner.y) / tanV);
			}
		}
	}
	return d * paddingFactor;
}
